import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import CustomCursor from '../components/CustomCursor';
import useSEO from '../hooks/useSEO';
import { FaShieldAlt, FaDatabase, FaUserLock, FaGlobe, FaChild } from 'react-icons/fa';

function PrivacyPolicy() {
  // Apply SEO metadata
  useSEO({
    title: "Privacy Policy - GLabs Automator",
    description: "Learn how GLabs Automator handles your data and protects your privacy. Our commitment to transparency and data security.",
    keywords: "privacy policy, data protection, user privacy, GLabs Automator",
    openGraph: {
      type: "website"
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: <FaDatabase className="text-4xl text-white" />,
      color: "bg-[#4285F4]",
      title: "Data Collection",
      id: "collection"
    },
    {
      icon: <FaShieldAlt className="text-4xl text-white" />,
      color: "bg-[#34A853]",
      title: "Data Security",
      id: "security"
    },
    {
      icon: <FaUserLock className="text-4xl text-white" />,
      color: "bg-[#EA4335]",
      title: "Your Rights",
      id: "rights"
    },
    {
      icon: <FaGlobe className="text-4xl text-white" />,
      color: "bg-[#FBBC04]",
      title: "Third Parties",
      id: "third-parties"
    }
  ];

  return (
    <div className="bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <CustomCursor />
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gray-50 dark:bg-gray-900 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse bg-[#4285F4]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-2xl animate-pulse bg-[#34A853]" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center animate-fadeInUp">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              Privacy <span className="text-[#34A853]">Policy</span>
            </h1>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-4">
              Last Updated: December 5, 2024
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Your privacy is important to us. Learn how we protect your data.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-12 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sections.map((section, index) => (
              <a
                key={index}
                href={`#${section.id}`}
                className="glass-card p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 text-center group ripple-effect animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`${section.color} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                  {section.icon}
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white">{section.title}</h3>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card rounded-3xl p-8 md:p-12 shadow-2xl space-y-12 animate-fadeInUp">
            
            {/* Introduction */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#4285F4]">🔒</span> Introduction
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Thank you for choosing GLabs Automator ("we", "us", or "our"), a Chrome extension designed to enhance your Google Labs AI experience with bulk content generation capabilities for ImageFX, Whisk, MusicFX, and Flow (VEO3). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Chrome extension.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                We respect your privacy and are committed to protecting it through our compliance with this policy. Please read this policy carefully to understand our practices regarding your information and how we will treat it.
              </p>
            </div>

            {/* Information We Collect */}
            <div id="collection">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#4285F4]">📊</span> Information We Collect
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                When you use GLabs Automator, we may collect the following types of information:
              </p>
              
              <div className="space-y-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-[#4285F4] p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Usage Data</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    We collect anonymized data about how you interact with our extension, including features used, performance metrics, and error logs. This helps us improve the extension's functionality and user experience.
                  </p>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-[#34A853] p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Local Storage Data</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    The extension temporarily stores your prompt inputs and configuration settings locally on your device to provide a smoother user experience. This data does not leave your browser and is not transmitted to our servers.
                  </p>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-[#FBBC04] p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Google Labs Interaction</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Our extension interacts with Google Labs tools (ImageFX, Whisk, MusicFX, Flow/VEO3) using your browser session. We do not access, store, or transmit your Google account credentials or any content generated through Google Labs tools.
                  </p>
                </div>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#34A853]">⚙️</span> How We Use Your Information
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-3 text-lg text-gray-700 dark:text-gray-300 ml-4">
                <li>Provide, maintain, and improve our extension</li>
                <li>Develop new features and functionality</li>
                <li>Diagnose and fix technical issues</li>
                <li>Monitor and analyze usage and trends</li>
                <li>Ensure the extension works seamlessly with Google Labs tools</li>
              </ul>
            </div>

            {/* Data Retention */}
            <div id="security">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#EA4335]">💾</span> Data Retention
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                GLabs Automator only stores your data locally on your device using browser storage. This data persists only until you clear your browser cache or uninstall the extension. We do not maintain any user-specific data on our servers.
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-[#EA4335] p-6 rounded-lg">
                <p className="text-gray-700 dark:text-gray-300 font-semibold">
                  Important: All your prompts, settings, and generated content remain on your device and are never uploaded to our servers.
                </p>
              </div>
            </div>

            {/* Data Security */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#FBBC04]">🔐</span> Data Security
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect the security of your information. However, no method of electronic storage is 100% secure. Therefore, while we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                Since all data is stored locally in your browser, you maintain full control over your information and can delete it at any time by clearing your browser data or uninstalling the extension.
              </p>
            </div>

            {/* Third-Party Services */}
            <div id="third-parties">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#4285F4]">🌐</span> Third-Party Services
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Our extension operates as an enhancement to Google Labs tools (ImageFX, Whisk, MusicFX, Flow/VEO3), which are subject to Google's own Privacy Policy. We recommend reviewing Google's Privacy Policy to understand how they handle your data when you use their services.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-[#4285F4] p-6 rounded-lg">
                <p className="text-gray-700 dark:text-gray-300 font-semibold mb-2">
                  We do not sell or rent your data to third parties.
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  We may use anonymized, aggregated data for product improvement purposes, but this data cannot be used to identify individual users.
                </p>
              </div>
            </div>

            {/* Your Rights */}
            <div id="rights">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#34A853]">✅</span> Your Rights
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul className="list-disc list-inside space-y-3 text-lg text-gray-700 dark:text-gray-300 ml-4">
                <li>The right to access information we have about you</li>
                <li>The right to correct or update your personal information</li>
                <li>The right to have your information deleted</li>
                <li>The right to object to processing of your personal information</li>
                <li>The right to data portability</li>
              </ul>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
                Since we do not store personal information on our servers, most of these rights can be exercised by managing your browser's local storage and cache settings.
              </p>
            </div>

            {/* Children's Privacy */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#FBBC04]">👶</span> Children's Privacy
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Our extension is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can take appropriate steps.
              </p>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-[#FBBC04] p-6 rounded-lg">
                <p className="text-gray-700 dark:text-gray-300 font-semibold">
                  Note: Google Labs tools may have their own age restrictions. Please review Google's terms of service for age-related requirements.
                </p>
              </div>
            </div>

            {/* Changes to This Privacy Policy */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#EA4335]">🔄</span> Changes to This Privacy Policy
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
              </p>
            </div>

            {/* Contact */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#4285F4]">📧</span> Contact Us
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <a
                href="https://intercom.help/connectautoplaylabs/en"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#4285F4] to-[#357ae8] text-white font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl ripple-effect"
              >
                Support Center
              </a>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-12 animate-fadeInUp" style={{ animationDelay: '200ms' }}>
            <Link to="/">
              <button className="glass-card px-8 py-4 rounded-2xl font-bold text-gray-900 dark:text-white hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl ripple-effect">
                ← Back to Home
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
